import { useEffect, useRef, useState } from 'react';
import { API } from '../service/ProductsService';
import ProductRow from './ProductRow';

const SEARCH_DEBOUNCE_MS = 400;

const s = {
  overlay:  { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 12px', zIndex: 100 },
  dialog:   { width: '100%', maxWidth: 1000, maxHeight: '92vh', display: 'flex', flexDirection: 'column', background: '#f4f5f7', borderRadius: 14, boxShadow: '0 16px 48px rgba(0,0,0,.25)', overflow: 'hidden' },
  header:   { background: '#fff', padding: '16px 18px 12px', borderBottom: '1px solid #e5e7eb' },
  titleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  title:    { margin: 0, fontSize: 20, fontWeight: 700 },
  subtitle: { margin: '2px 0 0', fontSize: 13, color: '#59636e' },
  close:    { background: 'none', border: 'none', fontSize: 26, lineHeight: 1, color: '#59636e', padding: '0 4px' },
  search:   { width: '100%', marginTop: 12, padding: '10px 12px', fontSize: 16, border: '1px solid #d0d7de', borderRadius: 10, background: '#fff' },
  count:    { marginTop: 8, fontSize: 13, color: '#59636e' },
  body:     { overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10 },
  message:  { textAlign: 'center', color: '#59636e', padding: '32px 16px' },
  error:    { background: '#fff1f0', border: '1px solid #ffccc7', color: '#a8071a', padding: '10px 14px', borderRadius: 10 },
  more:     { alignSelf: 'center', margin: '6px 0 10px', padding: '10px 22px', border: '1px solid #1a3f9c', borderRadius: 10, background: '#fff', color: '#1a3f9c', fontWeight: 600 },
};

// חלון עם כל המוצרים שמאחורי משבצת בדשבורד, עם חיפוש ו"טען עוד" (50 בכל פעם, spGetProductList).
// list / storeId - ר' API.getProductList. onClose - כפתור ×, Escape, לחיצה על הרקע.
const ProductListModal = ({ title, subtitle, list, storeId, onClose }) => {
  const [q, setQ] = useState('');
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [pricesByItem, setPricesByItem] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestId = useRef(0);   // תשובה ישנה (חיפוש קודם) לא דורסת חדשה

  // נועל את גלילת העמוד שמאחורי החלון, ו-Escape סוגר
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // append=false: עמוד ראשון (פתיחה / חיפוש חדש); true: "טען עוד"
  const load = async (query, pageNum, append) => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const resp = await API.getProductList(list, storeId, query.trim(), pageNum);
      if (id !== requestId.current) return;
      const status = resp?.[0]?.[0];
      if (status?.code !== 0) {
        setError(status?.msg || 'שגיאה בטעינת המוצרים');
        return;
      }
      // [1] stores, [2] items, [3] prices - כמו בחיפוש
      const byItem = {};
      (resp[3] || []).forEach((p) => {
        if (!byItem[p.itemId]) byItem[p.itemId] = {};
        byItem[p.itemId][p.storeId] = p;
      });
      const newItems = resp[2] || [];
      setStores(resp[1] || []);
      setItems((prev) => (append ? [...prev, ...newItems] : newItems));
      setPricesByItem((prev) => (append ? { ...prev, ...byItem } : byItem));
      setTotalCount(newItems[0]?.totalCount ?? (append ? totalCount : 0));
      setPage(pageNum);
    } catch (err) {
      if (id === requestId.current) setError(err.message || 'שגיאה בטעינת המוצרים');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  // פתיחה וחיפוש (אחרי הפסקה קצרה בהקלדה)
  useEffect(() => {
    const timer = setTimeout(() => load(q, 0, false), q ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(timer);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasMore = items.length < totalCount;

  return (
    <div style={s.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={s.dialog} role="dialog" aria-modal="true" aria-labelledby="plist-title">
        <div style={s.header}>
          <div style={s.titleRow}>
            <div>
              <h2 id="plist-title" style={s.title}>{title}</h2>
              {subtitle && <p style={s.subtitle}>{subtitle}</p>}
            </div>
            <button style={s.close} onClick={onClose} aria-label="סגירה">×</button>
          </div>
          <input
            type="search"
            style={s.search}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="חיפוש ברשימה לפי שם מוצר או חברה"
            autoFocus
          />
          <div style={s.count}>
            {loading && items.length === 0 ? 'טוען…' : `${totalCount.toLocaleString('he-IL')} מוצרים${q.trim() ? ` עבור "${q.trim()}"` : ''}`}
          </div>
        </div>

        <div style={s.body}>
          {error && <div style={s.error}>{error}</div>}

          {!loading && !error && items.length === 0 && (
            <div style={s.message}>{q.trim() ? `לא נמצאו מוצרים עבור "${q.trim()}"` : 'אין מוצרים ברשימה'}</div>
          )}

          {items.map((item) => (
            <ProductRow key={item.id} item={item} stores={stores} prices={pricesByItem[item.id] || {}} />
          ))}

          {hasMore && (
            <button style={s.more} onClick={() => load(q, page + 1, true)} disabled={loading}>
              {loading ? 'טוען…' : `טען עוד (${(totalCount - items.length).toLocaleString('he-IL')} נוספים)`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductListModal;
